import { danger } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { getAccessParams } from 'utils/shared-access';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { __t, applyTranslations } from 'utils/language';
import { snakeCaseKeys } from 'utils/keys';

import type { Action, Dispatch, GetState, ThunkAction } from 'types';
import type {
  SharedAccessProductListFilters,
  SharedAccessProductListType
} from '../types/SharedAccessProductList.type';

applyTranslations(require('./shared-access-product-list.i18n.json'));

const RESET = 'sharedAccessProductList/RESET';
const LOADED = 'sharedAccessProductList/LOADED';
const SET_LOADING_STATE = 'sharedAccessProductList/SET_LOADING_STATE';

type State = {
  products: Array<SharedAccessProductListType>,
  currentPage: number,
  pagesTotal: number,
  isLoading: boolean
};

const initialState: State = {
  products: [],
  currentPage: 1,
  pagesTotal: -1,
  isLoading: false
};

export function resetSharedAccessProductList(): Action {
  return {
    type: RESET
  };
}

function dataLoaded(
  products: Array<SharedAccessProductListType>,
  currentPage: number,
  pagesTotal: number
): Action {
  return {
    type: LOADED,
    payload: {
      products,
      currentPage,
      pagesTotal
    }
  };
}

function setLoadingState(loadingState: boolean): Action {
  return {
    type: SET_LOADING_STATE,
    payload: {
      loadingState
    }
  };
}

export function fetchSharedAccessProducts(page: number, filters: SharedAccessProductListFilters): ThunkAction {
  return async (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const { config, options } = getAccessParams(getState);
    const queryString = Querystring.objectToString({
      page,
      ...snakeCaseKeys(filters)
    });
    const url = API.END_POINTS.SHARED_PRODUCT_LIST.sharedProductListItems(queryString);

    dispatch(setLoadingState(true));

    try {
      const { data } = await get(url, config, options);

      dispatch(dataLoaded(data.data, data.meta.currentPage, data.meta.pagesTotal));
    } catch (e) {
      logError(e);
      dispatch(danger(__t('shared_access_product_list_reducer.error_list_items')));
    } finally {
      dispatch(setLoadingState(false));
    }
  };
}

export default createReducer<State, any>('sharedAccessProductList', initialState, {
  [RESET]: () => initialState,
  [SET_LOADING_STATE]: (state, { loadingState }) => ({
    ...state,
    isLoading: loadingState
  }),
  [LOADED]: (state, { products, currentPage, pagesTotal }) => ({
    ...state,
    products: (currentPage === 1) ? products : state.products.concat(products),
    currentPage,
    pagesTotal
  })
});
